import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/common/Navbar';
import { HELP_ARTICLES, HELP_SECTIONS } from '../content/helpArticles';
import './HelpPage.css';

// Блоки рендерятся по типу, а не как HTML из строки: справку нельзя
// случайно превратить в дыру, вставив в текст разметку.
const Block = ({ block }) => {
  switch (block.t) {
    case 'ul':
      return <ul className="help-list">{block.x.map((i) => <li key={i}>{i}</li>)}</ul>;
    case 'ol':
      return <ol className="help-list">{block.x.map((i) => <li key={i}>{i}</li>)}</ol>;
    case 'note':
      return <p className="help-note">{block.x}</p>;
    case 'warn':
      return <p className="help-warn">{block.x}</p>;
    default:
      return <p>{block.x}</p>;
  }
};

const articleText = (a) =>
  [a.title, a.keywords, ...a.body.map((b) => (Array.isArray(b.x) ? b.x.join(' ') : b.x))]
    .join(' ')
    .toLowerCase();

const HelpPage = () => {
  const { user, logout } = useAuth();
  const [query, setQuery] = useState('');
  const { hash } = useLocation();

  // Поиск сразу по всему тексту статьи, а не только по заголовку: человек
  // ищет словами своей проблемы («нули», «не грузится»), а не названием
  // раздела, в котором она описана.
  const found = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return HELP_ARTICLES;
    const words = q.split(/\s+/);
    return HELP_ARTICLES.filter((a) => {
      const text = articleText(a);
      return words.every((w) => text.includes(w));
    });
  }, [query]);

  const [openId, setOpenId] = useState(() => (hash ? hash.slice(1) : null));

  return (
    <div className="help-container">
      <Navbar user={user} isAdmin={user?.role === 'admin'} onLogout={logout} />

      <div className="help-page">
      <header className="help-head">
        <h1>Справка</h1>
        <p className="help-sub">
          Как работают разделы и что делать, когда что-то идёт не так.
          Не нашли ответ — напишите в поддержку, раздел рядом в меню.
        </p>
        <input
          type="search"
          className="help-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Поиск: фудкост, забыл пароль, нет данных…"
          aria-label="Поиск по справке"
        />
      </header>

      {found.length === 0 && (
        <p className="help-empty">
          По запросу «{query}» ничего не нашлось. Попробуйте одно слово вместо
          фразы — или напишите в поддержку, мы дополним справку.
        </p>
      )}

      {HELP_SECTIONS.map((section) => {
        const articles = found.filter((a) => a.section === section.id);
        if (articles.length === 0) return null;
        return (
          <section key={section.id} className="help-section">
            <h2>{section.title}</h2>
            {articles.map((a) => {
              const open = openId === a.id || Boolean(query.trim());
              return (
                <article key={a.id} id={a.id} className={`help-article${open ? ' open' : ''}`}>
                  <button
                    type="button"
                    className="help-article-head"
                    aria-expanded={open}
                    onClick={() => setOpenId(openId === a.id ? null : a.id)}
                  >
                    <span>{a.title}</span>
                    <span className="help-chevron" aria-hidden="true">{open ? '−' : '+'}</span>
                  </button>
                  {open && (
                    <div className="help-article-body">
                      {a.body.map((b, i) => <Block key={i} block={b} />)}
                    </div>
                  )}
                </article>
              );
            })}
          </section>
        );
      })}
      </div>
    </div>
  );
};

export default HelpPage;
